import React from 'react';
import { NavLink } from 'react-router-dom';
import {
  LayoutDashboard,
  Users,
  Bot,
  Facebook,
  MessageCircle,
  Megaphone,
  MessageSquare,
  Inbox,
  HelpCircle,
  UserPlus,
  UsersRound,
  FileText,
  BarChart3,
  Settings,
  User,
} from 'lucide-react';
import { ROLE_KEYS, getSessionRole } from '../../utils/roleUtils';

const clientMenuItems = [
  { path: '/', label: 'Dashboard', icon: LayoutDashboard, end: true },
  { path: '/client/users', label: 'Users', icon: Users },
  { path: '/bots', label: 'Bots', icon: Bot },
  // Access these pages from Settings instead of the main sidebar.
  { path: '/broadcast', label: 'Broadcasts', icon: Megaphone },
  { path: '/messages', label: 'Messages', icon: MessageSquare },
  { path: '/inbox', label: 'Inbox Management', icon: Inbox },
  { path: '/chat', label: 'Live Chat', icon: MessageCircle },
  { path: '/faq', label: 'FAQ', icon: HelpCircle },
  { path: '/onboard', label: 'Onboard', icon: UserPlus },
  { path: '/customers', label: 'Customers', icon: UsersRound },
  { path: '/templates', label: 'Templates', icon: FileText },
  { path: '/reports', label: 'Reports', icon: BarChart3 },
  { path: '/settings', label: 'Settings', icon: Settings },
];

const adminMenuItems = [
  { path: '/admin/dashboard', label: 'Admin Dashboard', icon: LayoutDashboard, end: true },
  { path: '/admin/clients', label: 'Clients', icon: UsersRound },
  { path: '/admin/users', label: 'Agents & Admins', icon: Users },
  { path: '/agent-admin/chat', label: 'Live Chat Control', icon: MessageCircle },
  { path: '/agent-admin/agents', label: 'Agent Details', icon: Users },
  { path: '/reports', label: 'Reports', icon: BarChart3 },
  { path: '/settings', label: 'Settings', icon: Settings },
];

const agentMenuItems = [
  { path: '/agent', label: 'Dashboard', icon: LayoutDashboard, end: true },
  { path: '/agent/chat', label: 'Live Chat', icon: MessageCircle },
  { path: '/agent/profile', label: 'Profile', icon: User },
];

const agentAdminMenuItems = [
  { path: '/agent-admin', label: 'Dashboard', icon: LayoutDashboard, end: true },
  { path: '/agent-admin/chat', label: 'Live Chat', icon: MessageCircle },
  { path: '/agent-admin/agents', label: 'Agent Details', icon: Users },
  { path: '/agent-admin/profile', label: 'Profile', icon: User },
];

const getMenuItemsForRole = (role) => {
  if (role === ROLE_KEYS.AGENT_ADMIN) return agentAdminMenuItems;
  if (role === ROLE_KEYS.AGENT) return agentMenuItems;
  if (role === ROLE_KEYS.ADMIN || role === ROLE_KEYS.SUPER_ADMIN) return adminMenuItems;
  return clientMenuItems;
};

const Sidebar = ({ drawerWidth, collapsedWidth = 70, open, onClose, isMobile, collapsed, session }) => {
  const handleItemClick = () => {
    if (isMobile && onClose) {
      onClose();
    }
  };

  const role = getSessionRole(session);
  const menuItems = getMenuItemsForRole(role);
  const currentWidth = collapsed ? collapsedWidth : drawerWidth;

  return (
    <>
      <style>
        {`
          /* Sidebar - Premium Light Theme */
          .sidebar {
            position: fixed;
            top: 0;
            left: 0;
            width: ${drawerWidth}px;
            height: 100vh;
            background: white;
            border-right: 1px solid rgba(37, 211, 102, 0.12);
            box-shadow:
              0 8px 28px rgba(37, 211, 102, 0.12),
              inset 0 1px 0 rgba(255, 255, 255, 0.8);
            z-index: 1001;
            transition: all 0.3s ease;
            overflow-y: auto;
            overflow-x: hidden;
            backdrop-filter: blur(10px);
            color: #0f172a;
          }

          /* Collapsed Sidebar */
          .sidebar.collapsed {
            width: ${collapsedWidth}px;
          }

          .sidebar.mobile-hidden {
            transform: translateX(-100%);
          }

          .sidebar.mobile-visible {
            transform: translateX(0);
          }

          /* Logo Section */
          .sidebar-logo {
            padding: 16px 20px;
            display: flex;
            align-items: center;
            justify-content: center;
            height: 70px;
            border-bottom: 1px solid rgba(37, 211, 102, 0.14);
          }

          .sidebar.collapsed .sidebar-logo {
            padding: 16px 10px;
          }

          .sidebar-logo img {
            height: 36px;
            transition: all 0.3s ease;
          }

          .sidebar.collapsed .sidebar-logo img {
            height: 32px;
          }

          /* Small logo icon for collapsed state */
          .sidebar-logo-icon {
            display: none;
          }

          .sidebar.collapsed .sidebar-logo img.full-logo {
            display: none;
          }

          .sidebar.collapsed .sidebar-logo-icon {
            display: block;
            width: 40px;
            height: 40px;
          }

          /* Menu */
          .sidebar-menu {
            padding: 10px 0;
            list-style: none;
            margin: 0;
          }

          .sidebar-menu-item {
            padding: 0;
            margin: 0;
          }

          /* Menu Link */
          .sidebar-menu-link {
            display: flex;
            align-items: center;
            padding: 12px 20px;
            color: #0f172a;
            text-decoration: none;
            font-size: 14px;
            font-weight: 500;
            transition: all 0.3s ease;
            cursor: pointer;
            position: relative;
            isolation: isolate;
          }

          .sidebar-menu-link:hover {
            background: linear-gradient(90deg, rgba(37, 211, 102, 0.16), rgba(37, 211, 102, 0.08));
            color: #064e3b;
            transform: translateX(2px);
            box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.9);
          }

          .sidebar-menu-link.active {
            color: #052e16;
            background: linear-gradient(110deg, #25d366, #1ebe5a);
            box-shadow:
              0 10px 30px rgba(37, 211, 102, 0.28),
              inset 0 1px 0 rgba(255, 255, 255, 0.85);
          }

          /* Menu Link - Collapsed */
          .sidebar.collapsed .sidebar-menu-link {
            padding: 14px 0;
            justify-content: center;
          }

          /* Menu Icon */
          .sidebar-menu-icon {
            font-size: 20px;
            margin-right: 12px;
            width: 24px;
            text-align: center;
            color: inherit;
          }

          .sidebar.collapsed .sidebar-menu-icon {
            font-size: 22px;
            margin-right: 0;
          }

          /* Menu Text */
          .sidebar-menu-text {
            flex: 1;
            white-space: nowrap;
            overflow: hidden;
          }

          .sidebar.collapsed .sidebar-menu-text {
            display: none;
          }

          /* Premium Scrollbar */
          .sidebar::-webkit-scrollbar {
            width: 8px;
          }

          .sidebar::-webkit-scrollbar-track {
            background: transparent;
          }

          .sidebar::-webkit-scrollbar-thumb {
            background: linear-gradient(180deg, #d6f8e1 0%, #c7f0d5 100%);
            border-radius: 8px;
            border: 2px solid rgba(255, 255, 255, 0.7);
          }

          .sidebar::-webkit-scrollbar-thumb:hover {
            background: linear-gradient(180deg, #d6f8e1 0%, #bfeccf 100%);
          }

          .sidebar {
            scrollbar-color: #d6f8e1 transparent;
            scrollbar-width: thin;
          }

          /* Overlay */
          .sidebar-overlay {
            position: fixed;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            background-color: rgba(0, 0, 0, 0.5);
            z-index: 1000;
            display: none;
          }

          .sidebar-overlay.visible {
            display: block;
          }

          /* Mobile */
          @media (max-width: 991px) {
            .sidebar {
              transform: translateX(-100%);
            }

            .sidebar.mobile-visible {
              transform: translateX(0);
              width: ${drawerWidth}px;
            }

            .sidebar.mobile-visible .sidebar-menu-link {
              padding: 12px 20px;
              justify-content: flex-start;
            }

            .sidebar.mobile-visible .sidebar-menu-icon {
              font-size: 20px;
              margin-right: 12px;
            }

            .sidebar.mobile-visible .sidebar-menu-text {
              display: block;
            }

            .sidebar.mobile-visible .sidebar-logo img.full-logo {
              display: block;
            }

            .sidebar.mobile-visible .sidebar-logo-icon {
              display: none;
            }
          }

          @media (min-width: 992px) {
            .sidebar {
              transform: translateX(0) !important;
            }
          }
        `}
      </style>

      {/* Overlay for mobile */}
      <div
        className={`sidebar-overlay ${isMobile && open ? 'visible' : ''}`}
        onClick={onClose}
      />

      {/* Sidebar */}
      <div className={`sidebar ${collapsed && !isMobile ? 'collapsed' : ''} ${isMobile ? (open ? 'mobile-visible' : 'mobile-hidden') : ''}`}>
        {/* Logo */}
        <div className="sidebar-logo">
          <img src="/assets/images/chat_logo.png" alt="ChattyBO" className="full-logo" />
          <img src="/assets/images/chat_logo_icon.png" alt="ChattyBO" className="sidebar-logo-icon" />
        </div>

        {/* Menu Items */}
        <ul className="sidebar-menu">
          {menuItems.map((item) => {
            const IconComponent = item.icon;
            return (
              <li key={item.path} className="sidebar-menu-item">
                <NavLink
                  to={item.path}
                  end={item.end}
                  className={({ isActive }) => `sidebar-menu-link ${isActive ? 'active' : ''}`}
                  onClick={(event) => {
                    event.preventDefault();
                    if (window.location.pathname !== item.path) {
                      window.location.assign(item.path);
                    }
                    handleItemClick();
                  }}
                  title={collapsed ? item.label : ''}
                >
                  <span className="sidebar-menu-icon">
                    <IconComponent size={20} />
                  </span>
                  <span className="sidebar-menu-text">{item.label}</span>
                </NavLink>
              </li>
            );
          })}
        </ul>
      </div>
    </>
  );
};

export default Sidebar;
